import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Container, Typography, Card, CardContent, Link, Stack } from '@mui/material';
import SiteFooter from '../components/SiteFooter';

export const CONTACT_EMAIL = 'lucas.a.lyon@gmail.com';
const UPDATED = 'October 27, 2026';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box sx={{ mt: 3 }}>
    <Typography variant="h6" component="h2" sx={{ mb: 1 }}>{title}</Typography>
    <Typography component="div" variant="body2" color="text.secondary" sx={{ '& p': { mb: 1.5 }, '& p:last-child': { mb: 0 }, '& ul': { mt: 0, pl: 2.5 }, '& li': { mb: 0.5 } }}>
      {children}
    </Typography>
  </Box>
);

const Shell: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 6 }, flex: 1 }}>
      <Typography variant="body2" sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/" color="inherit" underline="hover">Secret Santa</Link>
      </Typography>
      <Card>
        <CardContent sx={{ p: { xs: 3, md: 5 } }}>
          <Typography variant="h3" component="h1" sx={{ color: 'primary.main' }}>{title}</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, mb: 1 }}>
            Last updated {UPDATED}
          </Typography>
          {children}
        </CardContent>
      </Card>
      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        <Link component={RouterLink} to="/privacy" color="inherit" underline="hover">Privacy policy</Link>
        <Link component={RouterLink} to="/terms" color="inherit" underline="hover">Terms</Link>
        <Link href={`mailto:${CONTACT_EMAIL}`} color="inherit" underline="hover">Contact</Link>
      </Stack>
    </Container>
    <SiteFooter />
  </Box>
);

export const PrivacyPolicy: React.FC = () => (
  <Shell title="Privacy policy">
    <Section title="Who runs this">
      <p>
        Secret Santa at secretsanta.family is a small gift-exchange app run by Lucas Lyon
        ("we"). Questions about this policy go to{' '}
        <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>.
      </p>
    </Section>

    <Section title="What we store">
      <ul>
        <li>Your email address, which is how you sign in and how we send match notices and reminders.</li>
        <li>A display name, if you set one. It appears next to gift ideas you suggest.</li>
        <li>A mobile number, only if you choose text reminders. We also store that you agreed to texts, whether the number was verified, and whether texts are currently on.</li>
        <li>What an organizer enters for a group: participant names and emails, households, people who should not draw each other, budget, and exchange date and place.</li>
        <li>Gift ideas (description, optional link, notes, and price), gift-preference survey answers, and anonymous questions between a giver and their person.</li>
        <li>The draw itself: who is giving to whom, whether you have opened your match, and a log of emails and texts already sent so we don't send the same reminder twice.</li>
        <li>Sign-in records. Magic links and text short links are stored as hashes and work once. Your browser keeps a sign-in token in local storage for about 30 days. We don't use advertising cookies.</li>
      </ul>
    </Section>

    <Section title="Who can see it">
      <ul>
        <li>Your match is shown to you and to the organizers of that group. It is never put in a text message.</li>
        <li>Gift ideas about someone are visible to the other people in that group, and hidden from the person they're about. An idea you write about yourself is the exception: you can see it too, so it works as a wishlist.</li>
        <li>Survey answers are visible to you, the group's organizers, and the person who drew you.</li>
        <li>Anonymous questions show the message only, not the sender's name, and only to the two people in that pair.</li>
        <li>Your phone number is not shown to other participants. Organizers can see the names and emails they added.</li>
      </ul>
    </Section>

    <Section title="Who else handles it">
      <p>
        The app runs on Google Cloud (the site, the database, and stored secrets). Email is sent through Gmail.
        If you opt in to texts, Twilio sends the verification code and the reminders. Those companies process the
        data only to provide that service. We don't sell personal information, and we don't share it for advertising.
      </p>
    </Section>

    <Section title="Texts">
      <p>
        Text reminders are optional. You turn them on at{' '}
        <Link component={RouterLink} to="/settings">Notification settings</Link> after checking a consent box and
        confirming a code sent to your phone. Messages are a match-ready notice and a few reminders per season.
        Message and data rates may apply. Reply STOP to opt out, or turn texts off in settings. Reply HELP, or
        email us, for help. Details are in the <Link component={RouterLink} to="/terms">terms</Link>.
      </p>
    </Section>

    <Section title="How long we keep it">
      <p>
        Group data stays until an organizer deletes the group. You can remove your phone number at any time, which
        also turns texts off. Signing out clears the token in your browser. Email the contact above to ask about
        your account.
      </p>
    </Section>

    <Section title="Children">
      <p>
        Organizers sometimes add family members, including children, as participants. If you add someone, you are
        responsible for having their permission, or a parent or guardian's permission if they are a child.
      </p>
    </Section>
  </Shell>
);

export const Terms: React.FC = () => (
  <Shell title="Terms and conditions">
    <Section title="The service">
      <p>
        Secret Santa (secretsanta.family) helps a group draw names and share gift ideas. It is provided as a
        personal, free tool by Lucas Lyon. By signing in or by organizing a group, you agree to these terms
        and the <Link component={RouterLink} to="/privacy">privacy policy</Link>.
      </p>
    </Section>

    <Section title="Your account">
      <p>
        There is no password. We email you a one-time link, and that link signs you in on that device.
        Use the email address your organizer added. You're responsible for keeping access to that inbox,
        and for not forwarding a match link to someone who shouldn't see it.
      </p>
    </Section>

    <Section title="Organizers">
      <p>
        If you create a group, you may add people by name and email, run the draw, and see the assignments.
        Only add people you know and who expect to be in the exchange. Co-organizers can do the same things
        except delete the group.
      </p>
    </Section>

    <Section title="Acceptable use">
      <p>
        Don't use the app to harass anyone, send spam, or reveal someone's match outside the group.
        Don't try to get into a group you weren't added to. We can remove a group or an account that breaks these terms.
      </p>
    </Section>

    <Section title="Text message program">
      <p>
        Program name: Secret Santa reminders. Messages come from a toll-free number and only contain a link
        to the app, plus which group they're about. They never include the name of the person you drew.
      </p>
      <ul>
        <li>How you opt in: sign in, open Notification settings, enter your mobile number, check the consent box, and enter the verification code we text you.</li>
        <li>What we send: a notice when the draw is done, and optional reminders later (for example, to add gift ideas).</li>
        <li>Frequency: a handful of messages per group per season. This is not a marketing list.</li>
        <li>Message and data rates may apply.</li>
        <li>Opt out: reply STOP to any message, or turn texts off (or remove your number) in Notification settings. Reply START to opt back in, or turn texts on again in settings.</li>
        <li>Help: reply HELP, or email <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>.</li>
      </ul>
      <p>
        Carriers are not liable for delayed or undelivered messages. Consent to texts is not a condition of
        using the rest of the app; email notices still work if you skip texts.
      </p>
    </Section>

    <Section title="No warranty">
      <p>
        The app is provided as is. We try to keep matches secret and the draw fair, but we don't guarantee
        the site will always be available or error-free. To the extent the law allows, we aren't liable for
        lost gifts, a spoiled surprise, or other damages from using the site.
      </p>
    </Section>

    <Section title="Changes">
      <p>
        If these terms change, the updated date at the top will change. Continuing to use the site after that
        means you accept the update. Questions: <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>.
      </p>
    </Section>
  </Shell>
);
